"use client";

import { Fragment, useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { getProductDisplayName } from "@/lib/product-display-name";
import ProductImage from "@/components/ui/ProductImage";
import Link from "next/link";
import { productHref } from "@/lib/product-links";
import { sourceCompactLabel, sourceDisplayName } from "@/lib/source-display";
import { apiGet } from "@/lib/api-client";
import type { PriceRow, Market, PriceListResponse, PriceListMeta, FetchPricesParams } from "@/lib/api";
import Pagination from "@/components/ui/Pagination";
import { StatusState } from "@/components/ui/StatusState";
import ExportButton from "@/components/ui/ExportButton";
import { trackDiscoveryEvent } from "@/lib/analytics";

interface PriceTableProps {
  initialPrices?: PriceRow[];
  initialPricePage?: PriceListResponse;
  markets: Market[];
  requestParams?: Pick<FetchPricesParams, "range" | "latestOnly" | "product" | "market" | "marketType" | "sort" | "unit" | "category">;
  initialCategory?: string;
  initialCity?: string;
  initialMarket?: string;
  initialUnit?: string;
  initialQuery?: string;
  syncUrl?: boolean;
  showExport?: boolean;
  yoyByMarket?: Record<string, number>;
  hideProductColumn?: boolean;
  hideMarketColumn?: boolean;
  hideCityColumn?: boolean;
  groupByProduct?: boolean;
}

type SortKey = "avg-desc" | "avg-asc" | "name-asc" | "date-desc";

const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: "avg-desc", label: "En yüksek ortalama fiyat" },
  { key: "avg-asc", label: "En düşük ortalama fiyat" },
  { key: "name-asc", label: "Ürün adı (A–Z)" },
  { key: "date-desc", label: "En güncel kayıt tarihi" },
] as const;

const RANGE_OPTIONS = [
  { value: "1d", label: "Son veri günü" },
  { value: "7d", label: "Son 7 gün" },
  { value: "30d", label: "Son 30 gün" },
  { value: "90d", label: "Son 90 gün" },
  { value: "365d", label: "Son 1 yıl" },
] as const;

const UNIT_OPTIONS = ["kg", "adet", "bag", "demet", "kasa", "koli", "paket", "ton", "litre"] as const;

function unitLabel(value: string): string {
  const labels: Record<string, string> = {
    kg: "Kg",
    adet: "Adet",
    bag: "Bağ",
    demet: "Demet",
    kasa: "Kasa",
    koli: "Koli",
    paket: "Paket",
    ton: "Ton",
    litre: "Litre",
  };
  return labels[value] ?? humanizeSlug(value);
}

// Bilinen kategoriler için insan-okunur etiket. Haritada olmayan slug'lar
// `humanizeSlug()` ile "kebab-case" → "Kebab Case" olur.
const CATEGORY_LABEL: Record<string, string> = {
  sebze: "Sebze",
  meyve: "Meyve",
  balik: "Balık",
  "sebze-meyve": "Sebze & Meyve",
  ithal: "İthal",
  "ithal-donuk": "İthal (Donuk)",
  "tatli-su": "Tatlı Su",
  kultur: "Kültür",
  bakliyat: "Bakliyat",
  hububat: "Hububat",
  "yagli-tohum": "Yağlı Tohum",
  "sert-kabuklu": "Sert Kabuklu",
  "sanayi-bitkisi": "Sanayi Bitkisi",
  diger: "Diğer",
};

// /hal-fiyatlari sayfasında tüm geçmiş kayıtlar sayfalandırılırken yalnızca fiyat
// geçmişinde gerçekten veri olan ana kategorileri gösteriyoruz.
const PRICE_TABLE_CATEGORY_SLUGS = [
  "sebze-meyve",
  "sebze",
  "meyve",
  "balik",
  "ithal",
] as const;

const CATEGORY_DOT: Record<string, string> = {
  sebze: "bg-green-400",
  meyve: "bg-orange-400",
  balik: "bg-sky-400",
  "sebze-meyve": "bg-lime-400",
  ithal: "bg-purple-400",
  "ithal-donuk": "bg-purple-300",
  "tatli-su": "bg-cyan-400",
  kultur: "bg-amber-400",
  bakliyat: "bg-yellow-400",
};

// Source key "izmir_sebzemeyve" → "izmir"; renklendirme familiyaya göre.
const SOURCE_FAMILY_BADGE: Record<string, string> = {
  izmir:     "border-green-300 bg-green-100 text-green-900 dark:border-green-500/30 dark:bg-green-500/15 dark:text-green-200",
  konya:     "border-amber-300 bg-amber-100 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-200",
  kayseri:   "border-red-300 bg-red-100 text-red-900 dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-200",
  eskisehir: "border-indigo-300 bg-indigo-100 text-indigo-900 dark:border-indigo-500/30 dark:bg-indigo-500/15 dark:text-indigo-200",
  denizli:   "border-pink-300 bg-pink-100 text-pink-900 dark:border-pink-500/30 dark:bg-pink-500/15 dark:text-pink-200",
  antalya:   "border-orange-300 bg-orange-100 text-orange-950 dark:border-orange-500/30 dark:bg-orange-500/15 dark:text-orange-200",
  ibb:       "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-200",
  hal:       "border-teal-300 bg-teal-100 text-teal-900 dark:border-teal-500/30 dark:bg-teal-500/15 dark:text-teal-200",
  manual:    "border-slate-300 bg-slate-100 text-slate-900 dark:border-white/10 dark:bg-white/10 dark:text-(--color-muted)",
};

function humanizeSlug(slug: string): string {
  if (CATEGORY_LABEL[slug]) return CATEGORY_LABEL[slug];
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toLocaleUpperCase("tr-TR") + w.slice(1))
    .join(" ");
}

function sourceFamily(key: string): string {
  const first = (key || "").split("_")[0];
  return first || "manual";
}

// Türkçe karakter duyarsız arama için normalize (toLocaleLowerCase tr-TR şart —
// aksi halde "İ" combining dot verir ve eşleşme bozulur).
function normalize(s: string): string {
  return s
    .toLocaleLowerCase("tr-TR")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");
}

function toPriceNumber(value: string | number | null | undefined): number {
  if (value == null || value === "") return NaN;
  const n = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(n) ? n : NaN;
}

function fmt(value: string | number | null | undefined): string {
  if (value == null || value === "") return "—";
  const n = toPriceNumber(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  const raw = iso.includes("T") ? iso : `${iso.slice(0, 10)}T12:00:00Z`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function sortRows(rows: PriceRow[], key: SortKey): PriceRow[] {
  const copy = [...rows];
  switch (key) {
    case "avg-asc":
      return copy.sort((a, b) => toPriceNumber(a.avgPrice) - toPriceNumber(b.avgPrice));
    case "avg-desc":
      return copy.sort((a, b) => toPriceNumber(b.avgPrice) - toPriceNumber(a.avgPrice));
    case "name-asc":
      return copy.sort((a, b) => a.productName.localeCompare(b.productName, "tr"));
    case "date-desc":
      return copy.sort((a, b) => b.recordedDate.localeCompare(a.recordedDate));
    default:
      return copy;
  }
}

/**
 * Fiyat tablosu (client component).
 *
 * NEDEN client: Filtreleme/sıralama/arama tarayıcıda çalışır. Kategori chip'leri
 * veriden türetilir (yeni kaynak eklendiğinde otomatik listeye girer). Arama
 * ürün adında çalışır, Türkçe karakter duyarsızdır.
 */
export default function PriceTable({
  initialPrices,
  initialPricePage,
  markets,
  requestParams,
  initialCategory,
  initialCity,
  initialMarket,
  initialUnit,
  initialQuery,
  syncUrl = false,
  showExport = false,
  yoyByMarket,
  hideProductColumn = false,
  hideMarketColumn = false,
  hideCityColumn = false,
  groupByProduct = false,
}: PriceTableProps) {
  const initialMeta = initialPricePage?.meta;
  const serverPagination = Boolean(initialPricePage);
  const extendedFilters = syncUrl || showExport;
  const isBorsaTable = requestParams?.marketType === "borsa";
  const isOfficialTable = requestParams?.marketType === "resmi";
  const marketColumnLabel = isBorsaTable ? "Borsa" : isOfficialTable ? "Kaynak" : "Hal";
  const priceTypeLabel = isBorsaTable ? "Borsa fiyatı" : isOfficialTable ? "Resmî alım fiyatı" : "Hal fiyatı";
  const initialSort = requestParams?.sort ?? "avg-desc";
  const defaultSort: SortKey = syncUrl ? "date-desc" : initialSort;
  const defaultRange = syncUrl ? "30d" : (requestParams?.range || "7d");
  const [prices, setPrices] = useState<PriceRow[]>(
    initialPricePage?.items ?? (Array.isArray(initialPrices) ? initialPrices : []),
  );
  const [meta, setMeta] = useState<PriceListMeta | null>(initialMeta ?? null);
  // Urun sayfasi aileyi toplar: ayni hal ayni gun birden fazla cesit yayinlayabilir
  // (Kayseri'de "Domates" 31, "Domates Organik Siyah" 90, "Domates Yerli" 17,50).
  // Urun sutunu gizliyken bu satirlar ayirt edilemiyordu; birden fazla cesit varsa
  // sutun "Cesit" basligiyla geri gelir.
  const multiVariant = useMemo(
    () => new Set(prices.map((row) => row.productSlug)).size > 1,
    [prices],
  );
  const hideProduct = hideProductColumn && !multiVariant;
  const visibleColumnCount =
    8 -
    Number(hideProduct) -
    Number(hideMarketColumn) -
    Number(hideCityColumn);
  const tableColumnCount = visibleColumnCount - Number(groupByProduct && !hideProduct);

  const [page, setPage] = useState<number>(initialMeta?.page ?? 1);
  const [pageSize, setPageSize] = useState<number>(initialMeta?.limit ?? 100);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const safePrices = Array.isArray(prices) ? prices : [];
  const safeMarkets = Array.isArray(markets) ? markets : [];

  const [city, setCity] = useState<string>(initialCity || "all");
  const [market, setMarket] = useState<string>(initialMarket || requestParams?.market || "all");
  const [category, setCategory] = useState<string>(initialCategory || "all");
  const [unit, setUnit] = useState<string>(initialUnit || requestParams?.unit || "all");
  const [range, setRange] = useState<string>(requestParams?.range || "7d");
  const [sort, setSort] = useState<SortKey>(initialSort);
  const [query, setQuery] = useState<string>(initialQuery || "");
  const deferredQuery = useDeferredValue(query);
  const previousFilterRef = useRef({
    queryLength: (initialQuery || "").trim().length,
    city: initialCity || "all",
    market: initialMarket || requestParams?.market || "all",
    category: initialCategory || "all",
    unit: initialUnit || requestParams?.unit || "all",
    range: requestParams?.range || "7d",
    sort: initialSort,
    pageSize: initialMeta?.limit ?? 100,
  });
  const zeroResultSignatureRef = useRef("");

  useEffect(() => {
    if (!serverPagination) return;
    setPage(1);
  }, [serverPagination, city, market, category, unit, range, sort, deferredQuery, pageSize]);

  useEffect(() => {
    if (!serverPagination) return;
    let cancelled = false;
    const params = {
      latestOnly: requestParams?.latestOnly ?? true,
      product: requestParams?.product,
      market: market === "all" ? requestParams?.market : market,
      marketType: requestParams?.marketType,
      page,
      limit: pageSize,
      sort,
      city: city === "all" ? undefined : city,
      category: category === "all" ? requestParams?.category : category,
      unit: unit === "all" ? undefined : unit,
      range,
      q: deferredQuery.trim() || undefined,
    };

    setLoading(true);
    setLoadError(false);
    apiGet<PriceListResponse>(groupByProduct ? "/prices/groups" : "/prices", params)
      .then((result) => {
        if (cancelled) return;
        setPrices(Array.isArray(result.items) ? result.items : []);
        setMeta(result.meta ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(true);
          console.error("[PriceTable] fiyatlar yüklenemedi", err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    serverPagination,
    page,
    pageSize,
    city,
    category,
    sort,
    deferredQuery,
    requestParams?.range,
    requestParams?.latestOnly,
    requestParams?.product,
    requestParams?.market,
    requestParams?.marketType,
    requestParams?.category,
    market,
    unit,
    range,
    retryToken,
    groupByProduct,
  ]);

  const cityOptions = useMemo(() => {
    const seen = new Set<string>();
    const list: { slug: string; name: string }[] = [];
    const source: ReadonlyArray<{ cityName: string }> =
      safeMarkets.length > 0 ? safeMarkets : safePrices;
    for (const item of source) {
      const key = item.cityName?.toLowerCase() ?? "";
      if (!key || seen.has(key)) continue;
      seen.add(key);
      list.push({ slug: key, name: item.cityName });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [safeMarkets, safePrices]);

  const marketOptions = useMemo(() => safeMarkets
    .filter((item) => city === "all" || item.cityName?.toLocaleLowerCase("tr-TR") === city)
    .map((item) => ({ slug: item.slug, name: item.name, cityName: item.cityName }))
    .sort((a, b) => a.name.localeCompare(b.name, "tr")), [safeMarkets, city]);

  const unitOptions = useMemo(() => {
    const values = new Set<string>(UNIT_OPTIONS);
    for (const row of safePrices) {
      if (row.unit?.trim()) values.add(row.unit.trim());
    }
    return [...values].sort((a, b) => unitLabel(a).localeCompare(unitLabel(b), "tr"));
  }, [safePrices]);

  // Kategori seçenekleri verideki gerçek dağılımdan türetilir — sayı ile.
  // Böylece balık, ithal, sebze-meyve gibi yeni kategoriler hard-code
  // edilmeden listeye girer.
  const categoryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of safePrices) {
      const key = p.categorySlug || "diger";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const categorySlugs = serverPagination && !requestParams?.marketType
      ? [...PRICE_TABLE_CATEGORY_SLUGS]
      : [...counts.keys()];
    const list = categorySlugs
      .map((slug) => ({ slug, label: humanizeSlug(slug), count: counts.get(slug) ?? 0 }))
      .sort((a, b) => a.label.localeCompare(b.label, "tr"));
    return [
      { slug: "all", label: "Tümü", count: meta?.total ?? safePrices.length },
      ...list,
    ];
  }, [safePrices, serverPagination, requestParams?.marketType, meta?.total]);

  const filtered = useMemo(() => {
    if (serverPagination) return safePrices;
    const nq = deferredQuery.trim() ? normalize(deferredQuery.trim()) : "";
    const rows = safePrices.filter((row) => {
      if (city !== "all" && row.cityName?.toLowerCase() !== city) return false;
      if (market !== "all" && row.marketSlug !== market) return false;
      if (category !== "all" && (row.categorySlug || "diger") !== category) return false;
      if (unit !== "all" && row.unit !== unit) return false;
      if (nq && !normalize(row.productName).includes(nq)) return false;
      return true;
    });
    return sortRows(rows, sort);
  }, [safePrices, city, market, category, unit, sort, deferredQuery, serverPagination]);

  const displayRows: Array<{
    row: PriceRow;
    group: { key: string; marketCount: number; rowCount: number } | null;
  }> = useMemo(
    () => filtered.map((row) => ({ row, group: null })),
    [filtered],
  );

  const resetFilters = () => {
    setCity("all");
    setMarket("all");
    setCategory("all");
    setUnit("all");
    setRange(defaultRange);
    setQuery("");
    setSort(defaultSort);
  };

  const hasActiveFilter =
    city !== "all" || market !== "all" || category !== "all" || unit !== "all" ||
    range !== defaultRange || query.trim() !== "" || sort !== defaultSort;
  const total = meta?.total ?? safePrices.length;
  const totalPages = meta?.totalPages ?? 1;
  const currentPage = meta?.page ?? page;
  const showingStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingEnd = Math.min(total, (currentPage - 1) * pageSize + filtered.length);
  const activeFilterCount = [
    deferredQuery.trim() !== "",
    city !== "all",
    market !== "all",
    category !== "all",
    unit !== "all",
    range !== defaultRange,
    sort !== defaultSort,
  ].filter(Boolean).length;
  const staleCount = filtered.filter((row) => row.isStale).length;
  const freshCount = filtered.length - staleCount;

  useEffect(() => {
    if (!syncUrl || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    for (const key of ["q", "city", "market", "category", "unit", "range", "sort", "page", "limit"]) {
      params.delete(key);
    }
    if (deferredQuery.trim()) params.set("q", deferredQuery.trim());
    if (city !== "all") params.set("city", city);
    if (market !== "all") params.set("market", market);
    if (category !== "all") params.set("category", category);
    if (unit !== "all") params.set("unit", unit);
    if (range !== defaultRange) params.set("range", range);
    if (sort !== defaultSort) params.set("sort", sort);
    if (page > 1) params.set("page", String(page));
    if (pageSize !== 100) params.set("limit", String(pageSize));
    const queryString = params.toString();
    const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);

    const next = {
      queryLength: deferredQuery.trim().length,
      city,
      market,
      category,
      unit,
      range,
      sort,
      pageSize,
    };
    const previous = previousFilterRef.current;
    const changed = (Object.keys(next) as Array<keyof typeof next>).find((key) => next[key] !== previous[key]);
    previousFilterRef.current = next;
    if (!changed) return;
    const filterName = changed === "queryLength" ? "query" : changed === "pageSize" ? "page_size" : changed;
    const rawValue = next[changed];
    trackDiscoveryEvent("price_filter_changed", {
      filter_name: filterName,
      filter_value: changed === "queryLength" ? `${rawValue}_chars` : String(rawValue).slice(0, 100),
      query_length: deferredQuery.trim().length,
      active_filter_count: activeFilterCount,
    });
  }, [
    syncUrl,
    deferredQuery,
    city,
    market,
    category,
    unit,
    range,
    sort,
    page,
    pageSize,
    defaultRange,
    defaultSort,
    activeFilterCount,
  ]);

  useEffect(() => {
    if (loading || loadError || total !== 0 || activeFilterCount === 0) return;
    const signature = [deferredQuery.trim().length, city, market, category, unit, range, sort].join("|");
    if (zeroResultSignatureRef.current === signature) return;
    zeroResultSignatureRef.current = signature;
    const filterName = deferredQuery.trim()
      ? "query"
      : market !== "all"
        ? "market"
        : city !== "all"
          ? "city"
          : category !== "all"
            ? "category"
            : unit !== "all"
              ? "unit"
              : range !== defaultRange
                ? "range"
                : "sort";
    trackDiscoveryEvent("price_filter_zero_results", {
      filter_name: filterName,
      query_length: deferredQuery.trim().length,
      active_filter_count: activeFilterCount,
      result_count: 0,
      zero_results: true,
    });
  }, [loading, loadError, total, activeFilterCount, deferredQuery, city, market, category, unit, range, sort, defaultRange]);

  const categoryHref = (slug: string) => {
    const params = new URLSearchParams();
    if (slug !== "all") params.set("category", slug);
    if (city !== "all") params.set("city", city);
    if (market !== "all") params.set("market", market);
    if (query.trim()) params.set("q", query.trim());
    if (unit !== "all") params.set("unit", unit);
    if (range !== defaultRange) params.set("range", range);
    if (sort !== defaultSort) params.set("sort", sort);
    if (pageSize !== 100) params.set("limit", String(pageSize));
    const qs = params.toString();
    const basePath = isBorsaTable || isOfficialTable ? "/borsa" : "/hal-fiyatlari";
    return qs ? `${basePath}?${qs}` : basePath;
  };

  return (
    <div className="space-y-5">
      {/* Filtre bar */}
      <div className="flex flex-col gap-3 rounded-[14px] border border-(--color-border) bg-(--color-surface) p-4">
        {/* Üst sıra — arama + hal/il + birim + tarih + sıralama */}
        <div className={extendedFilters
          ? "grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1.4fr)_repeat(5,minmax(135px,1fr))]"
          : "grid gap-3 md:grid-cols-[minmax(260px,1fr)_minmax(150px,0.5fr)_minmax(190px,0.6fr)]"}
        >
          <div className="relative">
            <svg
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-(--color-muted)"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx={11} cy={11} r={7} />
              <path d="m20 20-3-3" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ürün adı ara… (ör. domates, biber, hamsi)"
              aria-label="Ürün ara"
              className="w-full rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) py-2 pl-9 pr-8 text-[13px] text-(--color-foreground) placeholder:text-(--color-muted) focus:border-(--color-brand) focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Aramayı temizle"
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-(--color-muted) hover:bg-(--color-border) hover:text-(--color-foreground)"
              >
                ×
              </button>
            )}
          </div>

          <select
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              setMarket("all");
            }}
            aria-label="Şehir"
            className="rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) px-3 py-2 text-[13px] text-(--color-foreground) focus:border-(--color-brand) focus:outline-none"
          >
            <option value="all">Tüm Şehirler</option>
            {cityOptions.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>

          {extendedFilters ? (
            <>
              <select
                value={market}
                onChange={(e) => setMarket(e.target.value)}
                aria-label={`${marketColumnLabel} veya kaynak`}
                className="min-w-0 rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) px-3 py-2 text-[13px] text-(--color-foreground) focus:border-(--color-brand) focus:outline-none"
              >
                <option value="all">Tüm {isBorsaTable ? "Borsalar" : isOfficialTable ? "Kaynaklar" : "Haller"}</option>
                {marketOptions.map((item) => (
                  <option key={item.slug} value={item.slug}>
                    {item.name}{city === "all" && item.cityName ? ` · ${item.cityName}` : ""}
                  </option>
                ))}
              </select>

              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                aria-label="Birim"
                className="min-w-0 rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) px-3 py-2 text-[13px] text-(--color-foreground) focus:border-(--color-brand) focus:outline-none"
              >
                <option value="all">Tüm Birimler</option>
                {unitOptions.map((value) => (
                  <option key={value} value={value}>{unitLabel(value)}</option>
                ))}
              </select>

              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                aria-label="Tarih aralığı"
                className="min-w-0 rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) px-3 py-2 text-[13px] text-(--color-foreground) focus:border-(--color-brand) focus:outline-none"
              >
                {RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </>
          ) : null}

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Sıralama"
            className="min-w-0 rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) px-3 py-2 text-[13px] text-(--color-foreground) focus:border-(--color-brand) focus:outline-none"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Alt sıra — kategori chip'leri (yatay scroll), sağda reset */}
        <div className="flex items-center gap-3">
          <div className="-mx-1 flex flex-1 gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {categoryOptions.map((c) => {
              const active = category === c.slug;
              const dot = c.slug !== "all" ? CATEGORY_DOT[c.slug] : null;
              const chipClass =
                "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors " +
                (active
                  ? "border-(--color-brand) bg-(--color-brand) text-(--color-brand-fg)"
                  : "border-(--color-border) bg-(--color-bg-alt) text-(--color-muted) hover:text-(--color-foreground)");
              const chipBody = (
                <>
                  {dot && (
                    <span
                      aria-hidden
                      className={`h-1.5 w-1.5 rounded-full ${dot}`}
                    />
                  )}
                  <span>{c.label}</span>
                  {(!serverPagination || c.slug === "all") && (
                    <span
                      className={
                        "font-(family-name:--font-mono) text-[10px] " +
                        (active ? "text-(--color-brand-fg)" : "text-(--color-muted)")
                      }
                    >
                      {c.count}
                    </span>
                  )}
                </>
              );
              // Sunucu sayfalamasinda chip gercek bir baglantidir (?category=). Istemci
              // filtresinde ise yalniz durum degistirir: href="#" bos baglanti yerine buton
              // (Tanitio 29 Eyl 2026 "bos # baglanti").
              return serverPagination ? (
                <Link
                  key={c.slug}
                  href={categoryHref(c.slug)}
                  prefetch={false}
                  onClick={() => setCategory(c.slug)}
                  className={chipClass}
                  aria-current={active ? "page" : undefined}
                >
                  {chipBody}
                </Link>
              ) : (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCategory(c.slug)}
                  className={chipClass}
                  aria-pressed={active}
                >
                  {chipBody}
                </button>
              );
            })}
          </div>

          {hasActiveFilter && (
            <button
              type="button"
              onClick={resetFilters}
              className="shrink-0 rounded-[8px] border border-(--color-border) bg-(--color-bg-alt) px-3 py-1.5 text-[12px] font-semibold text-(--color-muted) transition-colors hover:text-(--color-foreground)"
            >
              Sıfırla
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-[12px] border border-(--color-border-soft) bg-(--color-bg-alt) px-4 py-3 text-xs text-(--color-muted) sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <strong className="text-(--color-foreground)">
            {total.toLocaleString("tr-TR")} {groupByProduct ? "ürün" : "kayıt"}
          </strong>
          <span>{showingStart}–{showingEnd} gösteriliyor</span>
          <span>{RANGE_OPTIONS.find((option) => option.value === range)?.label ?? range}</span>
          {meta?.latestRecordedDate ? <span>Son kayıt: {formatDate(meta.latestRecordedDate)}</span> : null}
          {filtered.length > 0 && staleCount === 0 ? <span className="font-semibold text-(--color-success)">Güncel kayıtlar</span> : null}
          {freshCount > 0 && staleCount > 0 ? <span className="font-semibold text-(--color-warning)">{staleCount} gecikmeli, {freshCount} güncel</span> : null}
          {filtered.length > 0 && staleCount === filtered.length ? <span className="font-semibold text-(--color-warning)">Tüm sonuçlar gecikmeli</span> : null}
        </div>
        {showExport ? (
          <ExportButton
            label="Filtreli CSV indir"
            params={{
              product: requestParams?.product,
              q: deferredQuery.trim() || undefined,
              city: city === "all" ? undefined : city,
              market: market === "all" ? requestParams?.market : market,
              marketType: requestParams?.marketType,
              category: category === "all" ? requestParams?.category : category,
              unit: unit === "all" ? undefined : unit,
              range,
              latestOnly: requestParams?.latestOnly,
            }}
          />
        ) : null}
      </div>

      {loadError ? (
        <div className="rounded-[14px] border border-(--color-danger)/30 bg-(--color-surface)">
          <StatusState
            kind="error"
            compact
            title="Fiyatlar yüklenemedi"
            description="Mevcut sonuçlar korunuyor. Bağlantıyı denetleyip yeniden deneyin."
            action={(
              <button
                type="button"
                onClick={() => setRetryToken((value) => value + 1)}
                className="rounded-lg bg-(--color-brand) px-4 py-2 text-xs font-bold text-(--color-brand-fg)"
              >
                Yeniden dene
              </button>
            )}
          />
        </div>
      ) : null}

      {/* Tablo / urun akordeonu */}
      {groupByProduct ? (
        <GroupedPriceAccordion
          groups={filtered}
          city={city}
          market={market}
          unit={unit}
          range={range}
          isBorsaTable={isBorsaTable}
        />
      ) : (
      <div className="relative overflow-hidden rounded-[14px] border border-(--color-border) bg-(--color-surface)">
        {loading && (
          <div className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden bg-(--color-border)">
            <div className="h-full w-1/3 animate-pulse bg-(--color-brand)" />
          </div>
        )}
        <div className="md:hidden">
          {filtered.length === 0 ? (
            <StatusState
              kind="empty"
              compact
              title={activeFilterCount > 0 ? "Filtrelere uyan kayıt bulunamadı" : "Henüz fiyat verisi yok"}
              description={activeFilterCount > 0 ? "Arama veya filtreleri değiştirerek tekrar deneyin." : "Yeni kaynak verisi geldiğinde bu liste güncellenecek."}
            />
          ) : (
            <div className="divide-y divide-(--color-border-soft)">
              {displayRows.map(({ row, group }) => (
                <Fragment key={row.id}>
                  {group ? (
                    <div className="border-t-8 border-[#e4ebe7] bg-[#10261d] px-4 py-4 text-white">
                      <Link href={productHref(row)} prefetch={false} className="flex items-center gap-3 text-white">
                        <ProductImage
                          slug={row.productSlug}
                          name={row.productName}
                          categorySlug={row.categorySlug}
                          imageUrl={row.imageUrl}
                          canonicalSlug={row.canonicalProduct}
                          size={52}
                          className="rounded-lg border-white/25 shadow-none"
                        />
                        <span className="min-w-0">
                          <span className="font-(family-name:--font-mono) text-[9px] font-bold uppercase tracking-[0.14em] text-white/60">Ürün</span>
                          <strong className="mt-0.5 block truncate text-lg">{getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName })}</strong>
                          <span className="mt-1 block text-xs font-semibold text-white/75">
                            {group.rowCount === group.marketCount ? `${group.marketCount} kaynaktan güncel fiyat` : `${group.marketCount} kaynak · ${group.rowCount} kayıt`}
                          </span>
                        </span>
                      </Link>
                      <p className="mt-3 border-t border-white/15 pt-3 text-xs leading-5 text-white/70">
                        Aşağıdaki {group.rowCount} fiyat kaydı yalnızca {getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName })} içindir.
                      </p>
                    </div>
                  ) : null}
                  <MobilePriceCard
                    row={row}
                    isBorsaTable={isBorsaTable}
                    priceTypeLabel={priceTypeLabel}
                    yearAgoAvg={yoyByMarket?.[row.marketSlug]}
                    hideProduct={groupByProduct ? true : hideProduct}
                    hideMarket={hideMarketColumn}
                    hideCity={hideCityColumn}
                  />
                </Fragment>
              ))}
            </div>
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-(--color-border) text-left">
              {!hideProduct && !groupByProduct && (
                <th className="px-4 py-3 font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                  {hideProductColumn ? "Çeşit" : "Ürün"}
                </th>
              )}
              {!hideMarketColumn && (
                <th className="px-4 py-3 font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                  {marketColumnLabel}
                </th>
              )}
              {!hideCityColumn && (
                <th className="px-4 py-3 font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                  Şehir
                </th>
              )}
              <th className="px-4 py-3 text-right font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                Min
              </th>
              <th className="px-4 py-3 text-right font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                Ort
              </th>
              <th className="px-4 py-3 text-right font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                Maks
              </th>
              <th className="px-4 py-3 font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                Tarih
              </th>
              <th className="px-4 py-3 font-(family-name:--font-mono) text-[11px] font-semibold uppercase tracking-[0.1em] text-(--color-muted)">
                Kaynak
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={tableColumnCount}
                  className="px-4 py-12 text-center text-[13px] text-(--color-muted)"
                >
                  {activeFilterCount > 0
                    ? "Filtrelere uyan kayıt bulunamadı."
                    : "Henüz fiyat verisi yok. Yeni kaynak verisi geldiğinde bu liste güncellenecek."}
                </td>
              </tr>
            ) : (
              displayRows.map(({ row, group }) => {
                const categoryKey = row.categorySlug || "diger";
                const dotClass = CATEGORY_DOT[categoryKey] ?? "bg-(--color-muted)";
                const family = sourceFamily(row.sourceApi);
                const sourceClass =
                  SOURCE_FAMILY_BADGE[family] ??
                  "border-slate-300 bg-slate-100 text-slate-900 dark:border-white/10 dark:bg-white/10 dark:text-slate-200";
                const yearAgoAvg = yoyByMarket?.[row.marketSlug];
                const currentAvg = toPriceNumber(row.avgPrice);
                const yoyPct =
                  yearAgoAvg && yearAgoAvg > 0 && Number.isFinite(currentAvg)
                    ? ((currentAvg - yearAgoAvg) / yearAgoAvg) * 100
                    : null;
                return (
                  <Fragment key={row.id}>
                    {group ? (
                      <tr className="border-t-[10px] border-white bg-[#10261d] text-white first:border-t-0">
                        <th colSpan={tableColumnCount} scope="rowgroup" className="p-0 text-left">
                          <div className="flex min-h-20 items-center justify-between gap-6 border-l-4 border-[#18a36b] px-4 py-3">
                            <Link href={productHref(row)} prefetch={false} className="inline-flex min-w-0 items-center gap-3 text-white">
                              <ProductImage
                                slug={row.productSlug}
                                name={row.productName}
                                categorySlug={row.categorySlug}
                                imageUrl={row.imageUrl}
                                canonicalSlug={row.canonicalProduct}
                                size={56}
                                className="rounded-lg border-white/25 shadow-none"
                              />
                              <span className="min-w-0">
                                <span className="font-(family-name:--font-mono) text-[9px] font-bold uppercase tracking-[0.14em] text-white/55">Ürün</span>
                                <strong className="mt-0.5 block truncate text-lg">{getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName })}</strong>
                                <span className="mt-1 block text-[11px] font-semibold text-white/70">
                                  {group.rowCount === group.marketCount ? `${group.marketCount} kaynaktan güncel fiyat` : `${group.marketCount} kaynak · ${group.rowCount} kayıt`}
                                </span>
                              </span>
                            </Link>
                            <span className="hidden max-w-sm text-right text-[11px] font-medium leading-5 text-white/65 lg:block">
                              Aşağıdaki {group.rowCount} fiyat satırı yalnızca {getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName })} içindir.
                            </span>
                          </div>
                        </th>
                      </tr>
                    ) : null}
                    <tr
                      className="border-b border-(--color-border)/50 transition-colors last:border-b-0 hover:bg-(--color-bg-alt)"
                    >
                    {!hideProduct && !groupByProduct && (
                      <td className="px-4 py-1">
                        <Link
                          href={productHref(row)}
                          prefetch={false}
                          className="flex items-center gap-2.5 text-[14px] font-semibold text-(--color-foreground) hover:text-(--color-brand)"
                        >
                          {/* Kucuk fotograf: ayni ad farkli hallerde tekrar
                              ederken satirlari gozle taramayi hizlandiriyor.
                              Kategori noktasi kaliyor — filtre cipleriyle ayni
                              renk kodunu tasiyor, fotograf onun yerine gecmez. */}
                          <ProductImage
                            slug={row.productSlug}
                            name={row.productName}
                            categorySlug={row.categorySlug}
                            imageUrl={row.imageUrl}
                            canonicalSlug={row.canonicalProduct}
                            size={48}
                            className="rounded-lg"
                          />
                          <span
                            aria-hidden
                            title={humanizeSlug(categoryKey)}
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
                          />
                          <span className="truncate">{getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName })}</span>
                        </Link>
                      </td>
                    )}
                    {!hideMarketColumn && (
                      <td className={groupByProduct ? "py-3.5 pl-8 pr-4" : "px-4 py-3.5"}>
                        <Link
                          href={`/hal/${row.marketSlug}`}
                          prefetch={false}
                          className="text-[13px] text-(--color-muted) hover:text-(--color-brand)"
                        >
                          {row.marketName}
                        </Link>
                      </td>
                    )}
                    {!hideCityColumn && (
                      <td className="px-4 py-3.5 text-[13px] text-(--color-muted)">
                        {row.cityName}
                      </td>
                    )}
                    <td className="px-4 py-3.5 text-right font-(family-name:--font-mono) text-[13px] text-(--color-muted)">
                      ₺{fmt(row.minPrice)}<span className="ml-1 text-[10px]">/{row.unit}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="font-(family-name:--font-mono) text-[15px] font-bold text-(--color-foreground)">
                          ₺{fmt(row.avgPrice)}
                          <span className="ml-1 text-[10px] font-medium text-(--color-muted)">/{row.unit}</span>
                        </span>
                        {row.isSynthetic || row.avgPriceMethod === "midpoint" ? (
                          <span className="text-[10px] text-(--color-muted)" title="Kaynak yalnız minimum ve maksimum fiyat yayımladığı için orta nokta hesaplandı; hacim ağırlıklı ortalama değildir.">
                            min–maks orta noktası
                          </span>
                        ) : null}
                        {yoyPct !== null && (
                          <span
                            title={`Geçen yıl aynı dönem: ₺${yearAgoAvg!.toFixed(2)}`}
                            className={
                              "font-(family-name:--font-mono) text-[10px] font-semibold " +
                              (yoyPct > 0 ? "text-(--trend-up)" : "text-(--trend-down)")
                            }
                          >
                            {yoyPct > 0 ? "+" : ""}
                            {yoyPct.toFixed(1)}% geçen yıla
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-right font-(family-name:--font-mono) text-[13px] text-(--color-muted)">
                      ₺{fmt(row.maxPrice)}<span className="ml-1 text-[10px]">/{row.unit}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 font-(family-name:--font-mono) text-[12px] text-(--color-muted)">
                      {formatDate(row.recordedDate)}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex min-w-[132px] flex-wrap gap-1.5">
                        <span
                          title={sourceDisplayName(row.sourceName, row.sourceApi)}
                          className={
                            "inline-flex items-center rounded-[5px] border px-2 py-0.5 font-(family-name:--font-mono) text-[10px] font-semibold uppercase tracking-[0.05em] " +
                            sourceClass
                          }
                        >
                          {sourceCompactLabel(row.sourceName, row.sourceApi)}
                        </span>
                        {row.isOfficialSource && (
                          <span className="inline-flex items-center rounded-[5px] border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-200">
                            Resmi kaynak
                          </span>
                        )}
                        {row.isStale && (
                          <span className="inline-flex items-center rounded-[5px] border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-950 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-200">
                            {isBorsaTable ? "Geçen sezon" : "Gecikmeli"}
                          </span>
                        )}
                        {row.sourceUrl && (
                          <a
                            href={row.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center rounded-[5px] border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-900 hover:border-sky-500 dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-sky-200 dark:hover:border-sky-300/50"
                          >
                            Doğrulanabilir
                          </a>
                        )}
                        <Link
                          href="/metodoloji"
                          prefetch={false}
                          className="inline-flex items-center text-[10px] font-semibold text-(--color-muted) underline underline-offset-2 hover:text-(--color-brand)"
                        >
                          Metodoloji
                        </Link>
                      </div>
                    </td>
                    </tr>
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {serverPagination ? (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          showingStart={showingStart}
          showingEnd={showingEnd}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 font-(family-name:--font-mono) text-[11px] uppercase tracking-[0.1em] text-(--color-muted)">
          <span>
            {filtered.length} / {safePrices.length} kayıt
          </span>
          <span>{isBorsaTable ? "Ticaret borsası kaydıdır; hal veya perakende fiyatı değildir." : isOfficialTable ? "Resmî alım fiyatıdır; borsa, hal veya perakende fiyatı değildir." : "Toptancı hal fiyatıdır, perakende değildir."}</span>
          {deferredQuery.trim() && (
            <span className="text-(--color-foreground)">
              &ldquo;{deferredQuery.trim()}&rdquo; için sonuçlar
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function GroupedPriceAccordion({
  groups,
  city,
  market,
  unit,
  range,
  isBorsaTable,
}: {
  groups: PriceRow[];
  city: string;
  market: string;
  unit: string;
  range: string;
  isBorsaTable: boolean;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [rowsByProduct, setRowsByProduct] = useState<Record<string, PriceRow[]>>({});
  const [loadingProducts, setLoadingProducts] = useState<Set<string>>(() => new Set());
  const [failedProducts, setFailedProducts] = useState<Set<string>>(() => new Set());
  const inFlightProducts = useRef(new Set<string>());

  useEffect(() => {
    setExpanded(new Set());
    setRowsByProduct({});
    setLoadingProducts(new Set());
    setFailedProducts(new Set());
    inFlightProducts.current.clear();
  }, [city, market, unit, range]);

  const loadRows = async (productSlug: string) => {
    if (inFlightProducts.current.has(productSlug) || rowsByProduct[productSlug]) return;
    inFlightProducts.current.add(productSlug);
    setLoadingProducts((current) => new Set(current).add(productSlug));
    setFailedProducts((current) => {
      const next = new Set(current);
      next.delete(productSlug);
      return next;
    });
    try {
      const params = {
        product: productSlug,
        city: city === "all" ? undefined : city,
        market: market === "all" ? undefined : market,
        unit: unit === "all" ? undefined : unit,
        range,
        latestOnly: true,
        sort: "date-desc",
        limit: 250,
        page: 1,
      };
      const fetchPage = async (page: number) => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 12_000);
        try {
          return await apiGet<PriceListResponse>("/prices", { ...params, page }, { signal: controller.signal });
        } finally {
          window.clearTimeout(timeoutId);
        }
      };
      const first = await fetchPage(1);
      const remaining = first.meta.totalPages > 1
        ? await Promise.all(
            Array.from({ length: first.meta.totalPages - 1 }, (_, index) =>
              fetchPage(index + 2),
            ),
          )
        : [];
      const rows = [first, ...remaining]
        .flatMap((result) => result.items)
        .sort((a, b) =>
          a.cityName.localeCompare(b.cityName, "tr") ||
          a.marketName.localeCompare(b.marketName, "tr") ||
          b.recordedDate.localeCompare(a.recordedDate),
        );
      setRowsByProduct((current) => ({ ...current, [productSlug]: rows }));
    } catch {
      setFailedProducts((current) => new Set(current).add(productSlug));
    } finally {
      inFlightProducts.current.delete(productSlug);
      setLoadingProducts((current) => {
        const next = new Set(current);
        next.delete(productSlug);
        return next;
      });
    }
  };

  const toggle = (productSlug: string) => {
    const isOpen = expanded.has(productSlug);
    setExpanded((current) => {
      const next = new Set(current);
      if (isOpen) next.delete(productSlug);
      else next.add(productSlug);
      return next;
    });
    if (!isOpen && !rowsByProduct[productSlug] && !loadingProducts.has(productSlug)) {
      void loadRows(productSlug);
    }
  };

  if (groups.length === 0) {
    return (
      <div className="rounded-[14px] border border-(--color-border) bg-(--color-surface)">
        <StatusState kind="empty" compact title="Filtrelere uyan ürün bulunamadı" description="Arama veya filtreleri değiştirerek tekrar deneyin." />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const key = group.canonicalProduct || group.productSlug;
        const isOpen = expanded.has(key);
        const isLoading = loadingProducts.has(key);
        const hasFailed = failedProducts.has(key);
        const rows = rowsByProduct[key] ?? [];
        const marketCount = rows.length > 0
          ? new Set(rows.map((row) => row.marketSlug)).size
          : group.groupMarketCount ?? 0;
        const priceCount = rows.length > 0 ? rows.length : group.groupPriceCount ?? 0;
        const summaryMin = toPriceNumber(group.minPrice);
        const summaryMax = toPriceNumber(group.maxPrice);
        const summaryAvg = toPriceNumber(group.avgPrice);
        const hasPriceSummary = Number.isFinite(summaryMin) && Number.isFinite(summaryMax) && summaryMax > 0;
        const panelId = `urun-halleri-${key.replace(/[^a-z0-9-]/gi, "-")}`;
        const productName = getProductDisplayName({
          displayName: group.productName,
          nameTr: group.productNameTr ?? group.productName,
        });

        return (
          <section key={key} className="relative rounded-[14px] border border-(--color-border) bg-(--color-surface)">
            <div className="grid md:grid-cols-[230px_minmax(0,1fr)]">
              <aside className="border-b border-(--color-border-soft) bg-[#edf4f0] p-4 pr-16 md:border-b-0 md:border-r md:p-5">
                <div className={isOpen ? "md:sticky md:top-32" : ""}>
                  <Link href={productHref(group)} prefetch={false} className="flex items-center gap-3">
                    <ProductImage
                      slug={group.productSlug}
                      name={group.productName}
                      categorySlug={group.categorySlug}
                      imageUrl={group.imageUrl}
                      canonicalSlug={group.canonicalProduct}
                      size={64}
                      className="rounded-xl"
                    />
                    <span className="min-w-0">
                      <span className="font-(family-name:--font-mono) text-[9px] font-bold uppercase tracking-[0.14em] text-(--color-muted)">Ürün</span>
                      <strong className="mt-1 block truncate text-lg text-(--color-foreground)">{productName}</strong>
                      <span className="mt-1 block text-xs text-(--color-muted)">
                        {marketCount} kaynak · {priceCount} kayıt
                      </span>
                      {hasPriceSummary ? (
                        <span className="mt-1.5 block font-(family-name:--font-mono) text-[11px] font-bold text-(--color-foreground) md:hidden">
                          ₺{fmt(summaryMin)} – ₺{fmt(summaryMax)} <span className="font-medium text-(--color-muted)">/{group.unit}</span>
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </div>
              </aside>

              <div className="min-w-0 max-md:contents">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onPointerEnter={() => void loadRows(key)}
                  onFocus={() => void loadRows(key)}
                  onClick={() => toggle(key)}
                  className="flex min-h-24 w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-(--color-bg-alt) max-md:absolute max-md:right-4 max-md:top-8 max-md:min-h-0 max-md:w-auto max-md:p-0"
                >
                  <span className="max-md:sr-only">
                    <strong className="block text-sm text-(--color-foreground)">
                      {isOpen ? "Kayıtları gizle" : "Tüm kayıtları göster"}
                    </strong>
                    <span className="mt-1 block text-xs leading-5 text-(--color-muted)">
                      {marketCount} {isBorsaTable ? "borsadan" : "halden"} gelen {priceCount} güncel fiyat kaydı
                    </span>
                  </span>
                  {hasPriceSummary ? (
                    <span className="ml-auto hidden min-w-52 text-right md:block">
                      <span className="block font-(family-name:--font-mono) text-[9px] font-bold uppercase tracking-[0.13em] text-(--color-muted)">Güncel fiyat aralığı</span>
                      <strong className="mt-1 block font-(family-name:--font-mono) text-base text-(--color-foreground)">
                        ₺{fmt(summaryMin)} – ₺{fmt(summaryMax)} <span className="text-[10px] font-medium text-(--color-muted)">/{group.unit}</span>
                      </strong>
                      {Number.isFinite(summaryAvg) ? (
                        <span className="mt-1 block text-[11px] text-(--color-muted)">{isBorsaTable ? "Borsa" : "Hal"} ortalaması ₺{fmt(summaryAvg)}</span>
                      ) : null}
                    </span>
                  ) : null}
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-(--color-border) text-(--color-foreground)">
                    <svg aria-hidden className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </span>
                </button>

                {isOpen ? (
                  <div id={panelId} className="col-span-full border-t border-(--color-border) md:col-span-1">
                    <div className="sticky top-[78px] z-20 flex items-center gap-2 border-b border-(--color-border-soft) bg-[#edf4f0]/95 px-4 py-2 shadow-sm backdrop-blur md:hidden">
                      <ProductImage
                        slug={group.productSlug}
                        name={group.productName}
                        categorySlug={group.categorySlug}
                        imageUrl={group.imageUrl}
                        canonicalSlug={group.canonicalProduct}
                        size={34}
                        className="rounded-lg"
                      />
                      <span className="min-w-0">
                        <strong className="block truncate text-sm text-(--color-foreground)">{productName}</strong>
                        <span className="block text-[10px] text-(--color-muted)">
                          {marketCount} kaynak · {priceCount} kayıt{hasPriceSummary ? ` · ₺${fmt(summaryMin)}–₺${fmt(summaryMax)}` : ""}
                        </span>
                      </span>
                    </div>
                    {isLoading ? (
                      <div className="px-5 py-8 text-sm text-(--color-muted)" role="status">Tüm {isBorsaTable ? "borsa" : "hal"} fiyatları yükleniyor…</div>
                    ) : hasFailed ? (
                      <div className="flex items-center justify-between gap-4 px-5 py-6">
                        <span className="text-sm text-(--color-danger)">{isBorsaTable ? "Borsa" : "Hal"} fiyatları yüklenemedi.</span>
                        <button type="button" onClick={() => void loadRows(key)} className="rounded-lg border border-(--color-border) px-3 py-2 text-xs font-bold">Yeniden dene</button>
                      </div>
                    ) : (
                      <>
                        <div className="md:hidden">
                          {rows.map((row) => (
                            <MobilePriceCard key={row.id} row={row} isBorsaTable={isBorsaTable} priceTypeLabel={isBorsaTable ? "Borsa fiyatı" : "Hal fiyatı"} hideProduct hideMarket={false} hideCity={false} />
                          ))}
                        </div>
                        <div className="hidden overflow-x-auto md:block">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="border-b border-(--color-border-soft) bg-(--color-bg-alt) text-left">
                                {[isBorsaTable ? "Borsa" : "Hal", "Şehir", "Min", "Ort", "Maks", "Tarih"].map((label, index) => (
                                  <th key={label} className={`px-4 py-2.5 font-(family-name:--font-mono) text-[10px] font-semibold uppercase tracking-[0.1em] text-(--color-muted) ${index >= 2 && index <= 4 ? "text-right" : ""}`}>{label}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row) => {
                                const variantName = getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName });
                                const showVariant = row.productSlug !== key;
                                return (
                                  <tr key={row.id} className="border-b border-(--color-border-soft) last:border-b-0 hover:bg-(--color-bg-alt)">
                                    <td className="px-4 py-3">
                                      <Link href={`/hal/${row.marketSlug}`} prefetch={false} className="text-[13px] font-semibold text-(--color-foreground) hover:text-(--color-brand)">{row.marketName}</Link>
                                      {showVariant ? <span className="mt-1 block text-[10px] text-(--color-muted)">{variantName}</span> : null}
                                    </td>
                                    <td className="px-4 py-3 text-[12px] text-(--color-muted)">{row.cityName}</td>
                                    <td className="px-4 py-3 text-right font-(family-name:--font-mono) text-[12px] text-(--color-muted)">₺{fmt(row.minPrice)}</td>
                                    <td className="px-4 py-3 text-right font-(family-name:--font-mono) text-[14px] font-bold text-(--color-foreground)">₺{fmt(row.avgPrice)}<span className="ml-1 text-[9px] font-medium text-(--color-muted)">/{row.unit}</span></td>
                                    <td className="px-4 py-3 text-right font-(family-name:--font-mono) text-[12px] text-(--color-muted)">₺{fmt(row.maxPrice)}</td>
                                    <td className="whitespace-nowrap px-4 py-3 font-(family-name:--font-mono) text-[11px] text-(--color-muted)">{formatDate(row.recordedDate)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function MobilePriceCard({
  row,
  isBorsaTable,
  priceTypeLabel,
  yearAgoAvg,
  hideProduct,
  hideMarket,
  hideCity,
}: {
  row: PriceRow;
  isBorsaTable: boolean;
  priceTypeLabel: string;
  yearAgoAvg?: number;
  hideProduct: boolean;
  hideMarket: boolean;
  hideCity: boolean;
}) {
  const currentAvg = toPriceNumber(row.avgPrice);
  const yoyPct = yearAgoAvg && yearAgoAvg > 0 && Number.isFinite(currentAvg)
    ? ((currentAvg - yearAgoAvg) / yearAgoAvg) * 100
    : null;
  const family = sourceFamily(row.sourceApi);

  return (
    <article className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-4">
        {!hideProduct ? (
          <ProductImage
            slug={row.productSlug}
            name={row.productName}
            categorySlug={row.categorySlug}
            imageUrl={row.imageUrl}
            canonicalSlug={row.canonicalProduct}
            size={40}
            className="mt-0.5 rounded-lg"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          {hideProduct ? (
            <p className="font-(family-name:--font-mono) text-[9px] font-bold uppercase tracking-[0.12em] text-(--color-muted)">{priceTypeLabel}</p>
          ) : null}
          {!hideProduct ? (
            <Link href={productHref(row)} prefetch={false} className="block truncate text-sm font-bold text-(--color-foreground) hover:text-(--color-brand)">
              {getProductDisplayName({ displayName: row.productName, nameTr: row.productNameTr ?? row.productName })}
            </Link>
          ) : null}
          {!hideMarket ? (
            <Link href={`/hal/${row.marketSlug}`} prefetch={false} className={`${hideProduct ? "mt-1 font-semibold text-(--color-foreground)" : "mt-1 text-(--color-muted)"} block truncate text-xs hover:text-(--color-brand)`}>
              {row.marketName}
            </Link>
          ) : null}
          {!hideCity ? <p className="mt-0.5 text-xs text-(--color-muted)">{row.cityName}</p> : null}
        </div>
        <div className="shrink-0 text-right">
          <p className="font-(family-name:--font-mono) text-lg font-bold text-(--color-foreground)">₺{fmt(row.avgPrice)}</p>
          <p className="text-[11px] text-(--color-muted)">/{row.unit}</p>
          {row.isSynthetic || row.avgPriceMethod === "midpoint" ? (
            <p className="mt-1 max-w-32 text-[10px] text-(--color-muted)">min–maks orta noktası</p>
          ) : null}
          {yoyPct !== null ? (
            <p className={`mt-1 text-[10px] font-semibold ${yoyPct > 0 ? "text-(--trend-up)" : "text-(--trend-down)"}`}>
              {yoyPct > 0 ? "↑" : "↓"} %{Math.abs(yoyPct).toFixed(1)} geçen yıla
            </p>
          ) : null}
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-2 rounded-lg bg-(--color-bg-alt) p-3 text-xs">
        <div><dt className="text-(--color-muted)">Min</dt><dd className="mt-1 font-(family-name:--font-mono) font-semibold text-(--color-foreground)">₺{fmt(row.minPrice)}</dd></div>
        <div><dt className="text-(--color-muted)">Ortalama</dt><dd className="mt-1 font-(family-name:--font-mono) font-semibold text-(--color-foreground)">₺{fmt(row.avgPrice)}</dd></div>
        <div><dt className="text-(--color-muted)">Maks</dt><dd className="mt-1 font-(family-name:--font-mono) font-semibold text-(--color-foreground)">₺{fmt(row.maxPrice)}</dd></div>
      </dl>

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-(--color-muted)">
        <time dateTime={row.recordedDate}>{formatDate(row.recordedDate)}</time>
        <span aria-hidden>·</span>
        <span title={sourceDisplayName(row.sourceName, row.sourceApi)} className="font-semibold">{sourceCompactLabel(row.sourceName, row.sourceApi)}</span>
        {row.isOfficialSource ? <span className="rounded-full border border-(--color-success)/35 bg-(--color-success-bg) px-2 py-0.5 text-(--color-success)">Resmi kaynak</span> : null}
        {row.isStale ? <span className="rounded-full border border-(--color-warning)/40 bg-(--color-warning-bg) px-2 py-0.5 text-(--color-warning)">{isBorsaTable ? "Geçen sezon" : "Gecikmeli"}</span> : null}
        {row.sourceUrl ? <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-(--color-info) underline underline-offset-2">Kaynağı doğrula</a> : null}
        <Link href="/metodoloji" prefetch={false} className="font-semibold underline underline-offset-2 hover:text-(--color-brand)">Metodoloji</Link>
      </div>
    </article>
  );
}
